import React, { useMemo, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faLayerGroup, faList, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import AddCategoryModal from '../modals/AddCategoryModal';
import EditCategoryModal from '../modals/EditCategoryModal';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminMetricStrip from '../shared/AdminMetricStrip';
import AdminNotice from '../shared/AdminNotice';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminSectionCard from '../shared/AdminSectionCard';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/route_pages/Categories.css';
import { addCategory, updateCategory, deleteCategory, reorderCategories } from '../../utils/menuService';
import { errorTranslations } from '../../utils/errorMessages';
import { useVenueId } from '../../hooks/useUserData';

const Categories = () => {
  const venueId = useVenueId();
  const { categories: allCategories, setCategories, menus, items } = useAdmin();
  const { getAccessTokenSilently } = useAuth0();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const venueMenu = useMemo(() => menus[0] || null, [menus]);

  const menuCategories = useMemo(() => {
    if (!venueMenu) {
      return [];
    }

    return allCategories
      .filter((category) => category.menuId === venueMenu.id && !category.isSystemCategory)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [allCategories, venueMenu]);

  const activeLinkedItemsCount = useMemo(
    () =>
      menuCategories.reduce(
        (count, category) => count + items.filter((item) => item.categoryId === category.id && item.status !== 'archived').length,
        0,
      ),
    [items, menuCategories],
  );

  const handleCategoryAdded = async (newCategory) => {
    try {
      setLoading(true);
      const addedCategory = await addCategory(venueId, { ...newCategory, menuId: venueMenu.id }, getAccessTokenSilently);
      setCategories([...allCategories, addedCategory]);
      setIsAddModalVisible(false);
      setNotice({
        tone: 'success',
        title: 'Kategori eklendi',
        message: `${addedCategory.name} menüye eklendi.`,
      });
    } catch (error) {
      console.error('Failed to add category:', error);
      setNotice({
        tone: 'error',
        title: 'Kategori eklenemedi',
        message: 'Kategori oluşturulurken bir sorun oluştu.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryUpdated = async (updatedCategory) => {
    try {
      setLoading(true);
      const response = await updateCategory(
        venueId,
        updatedCategory.id,
        { ...updatedCategory, menuId: parseInt(updatedCategory.menuId, 10) },
        getAccessTokenSilently,
      );
      setCategories(allCategories.map((category) => (category.id === response.id ? response : category)));
      setIsEditModalVisible(false);
      setNotice({
        tone: 'success',
        title: 'Kategori güncellendi',
        message: `${response.name} için değişiklikler kaydedildi.`,
      });
    } catch (error) {
      console.error('Failed to update category:', error);
      setNotice({
        tone: 'error',
        title: 'Kategori güncellenemedi',
        message: errorTranslations[error.message] || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryDeleted = async (categoryId) => {
    const response = await deleteCategory(venueId, categoryId, getAccessTokenSilently);
    setCategories(allCategories.filter((category) => category.id !== categoryId));

    if (response.archivedItemsCount > 0) {
      setNotice({
        tone: 'success',
        title: 'Kategori silindi',
        message: `${response.archivedItemsCount} ürün arşivlenerek kategorisiz alana taşındı.`,
      });
    } else {
      setNotice({
        tone: 'success',
        title: 'Kategori silindi',
        message: 'Kategori başarıyla kaldırıldı.',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) {
      return;
    }

    try {
      setLoading(true);
      setNotice(null);
      await handleCategoryDeleted(categoryToDelete.id);
      setShowDeleteConfirmation(false);
      setCategoryToDelete(null);
    } catch (error) {
      setNotice({
        tone: 'error',
        title: 'Kategori silinemedi',
        message: errorTranslations[error.message] || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const getCategoryItems = (categoryId) => items.filter((item) => item.categoryId === categoryId && item.status !== 'archived');

  const getDeleteConsequences = (category) => {
    const categoryItems = getCategoryItems(category.id);
    const consequences = ['Kategori tamamen silinecek.'];

    if (categoryItems.length > 0) {
      consequences.unshift(`${categoryItems.length} ürün "Kategorilendirilmemiş" alanına taşınacak.`);
    }

    return consequences;
  };

  const handleMoveCategory = async (category, direction) => {
    const currentIndex = menuCategories.findIndex((entry) => entry.id === category.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= menuCategories.length) {
      return;
    }

    try {
      setLoading(true);
      const reorderedCategories = Array.from(menuCategories);
      [reorderedCategories[currentIndex], reorderedCategories[newIndex]] = [reorderedCategories[newIndex], reorderedCategories[currentIndex]];
      const categoriesWithNewOrder = reorderedCategories.map((entry, index) => ({ id: entry.id, sort_order: index + 1 }));
      await reorderCategories(venueId, categoriesWithNewOrder, getAccessTokenSilently);

      const updatedCategories = allCategories.map((entry) => {
        const newOrder = categoriesWithNewOrder.find((candidate) => candidate.id === entry.id);
        return newOrder ? { ...entry, sort_order: newOrder.sort_order } : entry;
      });

      setCategories(updatedCategories);
    } catch (error) {
      console.error('Failed to move category:', error);
      setNotice({
        tone: 'error',
        title: 'Sıralama güncellenemedi',
        message: 'Kategori sırası kaydedilemedi.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!venueMenu) {
    return (
      <div className="page-container">
        <AdminPageHeader title="Kategori Yönetimi" subtitle="Menü kategorilerini düzenleyin ve ürün akışını daha okunur hale getirin." />
        <AdminEmptyState
          icon={faLayerGroup}
          title="Henüz menü bulunamadı"
          description="Kategori eklemek için önce bir menü oluşturulması gerekiyor."
        />
      </div>
    );
  }

  const metrics = [
    {
      label: 'Kategori',
      value: menuCategories.length,
      helper: 'Sistem kategorileri hariç toplam kategori',
    },
    {
      label: 'Bağlı Ürün',
      value: activeLinkedItemsCount,
      helper: 'Arşiv dışı ürünler üzerinden hesaplandı',
    },
  ];

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Kategori Yönetimi"
        subtitle={`"${venueMenu.name}" menüsü için kategori sırasını ve içerik başlıklarını yönetin.`}
        actions={(
          <button className="btn btn-primary" onClick={() => { setNotice(null); setIsAddModalVisible(true); }}>
            <FontAwesomeIcon icon={faPlus} />
            Yeni Kategori
          </button>
        )}
      />

      {notice ? (
        <AdminNotice
          tone={notice.tone}
          title={notice.title}
          message={notice.message}
          onDismiss={() => setNotice(null)}
          autoDismissMs={notice.tone === 'success' ? 5000 : undefined}
        />
      ) : null}

      <AdminMetricStrip items={metrics} />

      <div className="admin-page-stack">
        <AdminSectionCard>
          {loading ? <LoadingSpinner /> : null}
          {menuCategories.length === 0 ? (
            <AdminEmptyState
              icon={faList}
              title="Henüz kategori bulunmuyor"
              description="Menünüzü daha yönetilebilir hale getirmek için ilk kategoriyi ekleyerek başlayın."
              action={(
                <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
                  <FontAwesomeIcon icon={faPlus} />
                  İlk Kategoriyi Ekle
                </button>
              )}
            />
          ) : (
            <div className="admin-management-list categories-management-list">
              {menuCategories.map((category, index) => {
                const categoryItems = getCategoryItems(category.id);

                return (
                  <article key={category.id} className="admin-management-row categories-row">
                    <div className="admin-management-leading">
                      <div className="admin-symbol-token categories-token">
                        <FontAwesomeIcon icon={faLayerGroup} />
                      </div>
                      <div className="admin-management-main">
                        <h3 className="admin-management-title">{category.name}</h3>
                        {category.description ? (
                          <p className="admin-management-subtitle">
                            {category.description}
                          </p>
                        ) : null}
                        <div className="admin-chip-row">
                          <span className="admin-chip admin-chip-accent">{categoryItems.length} aktif ürün</span>
                          <span className="admin-chip admin-chip-muted">Sıra #{index + 1}</span>
                        </div>
                      </div>
                    </div>

                    <div className="admin-management-meta">
                      <div className="admin-stat-stack">
                        <div className="admin-stat-card">
                          <strong>{categoryItems.length}</strong>
                          <span>Aktif ürün</span>
                        </div>
                        <div className="admin-stat-card">
                          <strong>{index + 1}</strong>
                          <span>Gösterim sırası</span>
                        </div>
                      </div>
                    </div>

                    <div className="admin-management-actions">
                      <div className="admin-reorder-cluster" aria-label={`${category.name} sıralama kontrolleri`}>
                        <div className="admin-reorder-buttons">
                          <button
                            type="button"
                            className="admin-reorder-button"
                            onClick={() => handleMoveCategory(category, 'up')}
                            disabled={loading || index === 0}
                            title="Kategoriyi yukarı taşı"
                            aria-label={`${category.name} kategorisini yukarı taşı`}
                          >
                            <FontAwesomeIcon icon={faChevronUp} />
                          </button>
                          <button
                            type="button"
                            className="admin-reorder-button"
                            onClick={() => handleMoveCategory(category, 'down')}
                            disabled={loading || index === menuCategories.length - 1}
                            title="Kategoriyi aşağı taşı"
                            aria-label={`${category.name} kategorisini aşağı taşı`}
                          >
                            <FontAwesomeIcon icon={faChevronDown} />
                          </button>
                        </div>
                      </div>

                      <div className="admin-action-cluster">
                        <button type="button" className="btn btn-secondary" onClick={() => { setCurrentCategory(category); setIsEditModalVisible(true); }}>
                          <FontAwesomeIcon icon={faEdit} />
                          Düzenle
                        </button>
                        <button type="button" className="btn btn-danger" onClick={() => { setCategoryToDelete(category); setShowDeleteConfirmation(true); }}>
                          <FontAwesomeIcon icon={faTrash} />
                          Sil
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </AdminSectionCard>
      </div>

      <AddCategoryModal show={isAddModalVisible} onClose={() => setIsAddModalVisible(false)} onCategoryAdded={handleCategoryAdded} />
      {currentCategory ? (
        <>
          <EditCategoryModal
            show={isEditModalVisible}
            onClose={() => setIsEditModalVisible(false)}
            category={currentCategory}
            onCategoryUpdated={handleCategoryUpdated}
          />
        </>
      ) : null}

      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Kategoriyi Sil"
        itemName={categoryToDelete?.name}
        message={`"${categoryToDelete?.name}" kategorisini silmek istediğinizden emin misiniz?`}
        consequences={categoryToDelete ? getDeleteConsequences(categoryToDelete) : []}
        loading={loading}
        type="danger"
      />
    </div>
  );
};

export default Categories;
